import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MapPin, Phone, User, Calendar, FileText, Wallet, AlertCircle } from "lucide-react";
import { STATUS_OPTIONS, statusPill, statusLabel, fmtEUR, fmtTL } from "../lib/api";

// Public page — no auth. Uses axios directly (not the credentialed instance).
export default function CustomerPortal() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const BASE = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || "http://localhost:8000";
  const API = `${BASE}/api`;

  useEffect(() => {
    axios.get(`${API}/public/portal/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Bilgilerinize ulaşılamadı. Lütfen bağlantıyı kontrol edin."));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <FullPage><div className="text-center max-w-md"><AlertCircle size={40} className="mx-auto text-red-500 mb-4" /><p className="text-zinc-600">{error}</p></div></FullPage>;
  if (!data) return <FullPage><div className="text-zinc-500 label-overline">Yükleniyor…</div></FullPage>;

  const totalPaid = data.currency === "TL" ? fmtTL(data.paid_tl) : fmtEUR(data.paid_eur);
  const totalRem = data.remaining_currency === "TL" ? fmtTL(data.remaining) : fmtEUR(data.remaining);
  const totalDisp = data.currency === "TL" ? fmtTL(data.total_display) : fmtEUR(data.total_display);

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="bg-zinc-950 text-white">
        <div className="max-w-3xl mx-auto p-6 md:p-8">
          <div className="label-overline text-white/60">VizePanel · Kişisel Portal</div>
          <h1 className="mt-2 font-display font-black text-3xl md:text-4xl">{data.full_name}</h1>
          <div className="mt-3 text-white/70 text-sm">
            {data.country} vize başvuru durumunuzu bu sayfadan takip edebilirsiniz.
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className={`status-pill ${statusPill(data.status)}`}>{statusLabel(data.status)}</span>
            <span className={`status-pill ${data.form_status === "dolu" ? "status-approved" : "status-appt"}`}>Form: {data.form_status === "dolu" ? "Dolu ✓" : "Boş"}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6 md:p-8 space-y-6">
        <Card icon={Calendar} title="Randevu Bilgileri">
          <Row label="Ülke" v={data.country} />
          <Row label="Seyahat Tarihi" v={data.travel_date || "—"} />
          <Row label="Randevu Tarihi" v={data.appointment_date || "—"} />
          <Row label="Randevu Saati" v={data.appointment_time || "—"} />
          {data.agency_name && <Row label="Randevu Yeri" v={`${data.agency_name}${data.agency_address ? " — " + data.agency_address : ""}`} />}
          {data.agency_google_maps && (
            <Row label="Konum" v={<a href={data.agency_google_maps} target="_blank" rel="noreferrer" className="text-[#0033cc] underline inline-flex items-center gap-1"><MapPin size={12}/>Google Maps'te aç</a>} />
          )}
        </Card>

        <Card icon={Wallet} title="Ödeme Durumu">
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Toplam" value={totalDisp} />
            <Metric label="Ödenen" value={totalPaid} tone="green" />
            <Metric label="Kalan" value={totalRem} tone={data.remaining > 0 ? "amber" : "green"} />
          </div>
          {data.payments.length > 0 && (
            <div className="mt-4">
              <div className="label-overline mb-2">Ödeme Geçmişi</div>
              <div className="space-y-2">
                {data.payments.map((p) => (
                  <div key={`${p.date}-${p.amount_eur}-${p.amount_tl}-${p.notes || ""}`} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2">
                    <span className="text-zinc-600">{(p.date || "").slice(0, 10)}</span>
                    <span className="font-semibold tabular-nums">{p.amount_eur ? fmtEUR(p.amount_eur) : fmtTL(p.amount_tl)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>

        {(data.supplier_name || data.supplier_phone) && (
          <Card icon={Phone} title="Danışman İletişim">
            {data.supplier_contact && <Row label="Yetkili" v={data.supplier_contact} />}
            {data.supplier_phone && <Row label="Telefon" v={<a href={`tel:${data.supplier_phone}`} className="text-[#0033cc] underline">{data.supplier_phone}</a>} />}
          </Card>
        )}

        {data.documents.length > 0 && (
          <Card icon={FileText} title="Yüklenen Belgeler">
            <div className="space-y-2">
              {data.documents.map((d) => (
                <div key={d.id} className="flex items-center gap-3 text-sm">
                  <CheckCircle2 size={14} className="text-[#00913c]" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{d.name}</div>
                    <div className="text-xs text-zinc-500">{d.kind}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="text-center text-xs text-zinc-400 py-6">VizePanel · Bu sayfa sadece sizinle paylaşılmıştır.</div>
      </div>
    </div>
  );
}

const FullPage = ({ children }) => <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">{children}</div>;
const Card = ({ icon: Icon, title, children }) => (
  <div className="grid-card p-6">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#0033cc]" />
      <h2 className="font-display font-bold text-lg">{title}</h2>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);
const Row = ({ label, v }) => (
  <div className="flex items-center justify-between text-sm py-1 border-b border-zinc-100 last:border-b-0">
    <span className="text-zinc-500">{label}</span>
    <span className="font-medium text-right ml-3">{v}</span>
  </div>
);
const Metric = ({ label, value, tone = "default" }) => {
  const c = { default: "text-zinc-950", green: "text-[#00913c]", amber: "text-[#8a5b00]" }[tone];
  return (
    <div className="text-center p-3 bg-zinc-50 rounded-md">
      <div className="label-overline">{label}</div>
      <div className={`mt-1 font-display font-black text-lg ${c}`}>{value}</div>
    </div>
  );
};
